import React, { useState, useRef, useEffect } from "react";
import { SITELOCATION_GLOBAL_STATE, MESSAGE_FLEET_GLOBAL_STATES, UPDATED_CAR_DATA_STATE, MAP_GLOBAL_STATES } from "globalStore/globalStore";
import { getFeaturesList, isUserSupport, isUserAdmin, getFeatureSetOption, carType } from "../../../constant";
import { MapCarIcon, TaggedCarIconComponent, LocationIcon } from "../MapIcons";
import { useHookstate } from "@hookstate/core";
import { useSelector, useDispatch } from "react-redux";
import { uuid } from "_helperFunctions/uuid";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";

export const CarMarkers = ({
    carData,
    mapData,
    mapsData,
    setEditModel,
    zoomPosition
}) => {
    const { siteLocationGlobalData } = useHookstate(SITELOCATION_GLOBAL_STATE);
    const { tagCarFromFleet } = useHookstate(MESSAGE_FLEET_GLOBAL_STATES);
    const { updatedCarData } = useHookstate(UPDATED_CAR_DATA_STATE);
    const {
        fleetCarsGlobalState,
        specialCarsGlobalState,
        memberCarsGlobalState,
        marshalCarsGlobalState,
        beverageCarsGlobalState,
        notInUseCarsGlobalState,
        carsNotCommunicatingGlobalState
    } = useHookstate(MAP_GLOBAL_STATES);

    const siteLocationGlobalDataValue = siteLocationGlobalData.get();
    const tagCarFromFleetValue = tagCarFromFleet.get();

    const { siteSettingsData, getUserRole, siteLocationsData, carProductData } = useSelector((state) => state.commonReducers) || [];
    const dispatch = useDispatch();

    const markerRefs = useRef({});
    const [carProductDataMap, setCarProductDataMap] = useState({});

    const [originalOverlaps, setOriginalOverlaps] = useState(null);
    const [overlaps, setOverlaps] = useState({});

    // Fetch product data for all cars
    useEffect(() => {
        if (carData && carData.length > 0) {
            carData.forEach((carItem) => {
                if (carItem?.Car?.GpsiCarId) {
                    dispatch(COMMON_SERVICES.getProductData(carItem.Car.GpsiCarId));
                }
            });
        }
    }, [carData, dispatch]);

    // Sync Redux carProductData to local map whenever it updates
    useEffect(() => {
        if (carProductData && carProductData.GpsiCarId) {
            setCarProductDataMap((prev) => ({
                ...prev,
                [carProductData.GpsiCarId]: carProductData
            }));
        }
    }, [carProductData]);

    useEffect(() => {
        if (Object.keys(updatedCarData.get()).length > 0 && Object.keys(markerRefs.current).length > 0) {
            var filteredCarData = JSON.parse(updatedCarData.get());

            if (!fleetCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.normal)
            }

            if (!specialCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.handicap)
            }

            if (!memberCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.member)
            }

            if (!marshalCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.marshal)
            }

            if (!beverageCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.bevcart)
            }

            if (!notInUseCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.notInUse)
            }

            if (!carsNotCommunicatingGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.notCommunicating)
            }

            const hasAllNullMarkerRefs = Object.values(markerRefs.current).every(x => x === null);
            const hasNullMarkerRefs = Object.values(markerRefs.current).some(x => x === null);

            if (!hasAllNullMarkerRefs && hasNullMarkerRefs) {
                setOverlaps(originalOverlaps);
            } else {
                const overlaps = calculateOverlaps(markerRefs, filteredCarData);
                setOverlaps(overlaps);

                if (originalOverlaps === null) {
                    setOriginalOverlaps(overlaps);
                }
            }
        }
    }, [zoomPosition,
        updatedCarData.get(),
        fleetCarsGlobalState.get(),
        specialCarsGlobalState.get(),
        memberCarsGlobalState.get(),
        marshalCarsGlobalState.get(),
        beverageCarsGlobalState.get(),
        notInUseCarsGlobalState.get(),
        carsNotCommunicatingGlobalState.get()
    ]);

    const shouldShowCarMarkers = () => {
        return ((!siteLocationGlobalDataValue && carData?.length !== 0) ||
            (tagCarFromFleetValue && !siteLocationGlobalDataValue && tagCarFromFleetValue.length === 0)) &&
            localStorage.getItem("mapCarsSelectAll") === "true" &&
            !(getFeatureSetOption(siteSettingsData && siteSettingsData?.Features, getFeaturesList().real_time_tracking) &&
                !(isUserAdmin(getUserRole && getUserRole.Role) || isUserSupport(getUserRole && getUserRole.Role)));
    }

    const shouldShowTagCar = () => {
        return !siteLocationGlobalDataValue && tagCarFromFleetValue && tagCarFromFleetValue !== 0;
    }

    const calculateOverlaps = (markerRefs, carDataList) => {
        const rects = {};

        // Step 1: get bounding rects
        Object.entries(markerRefs.current).forEach(([carNumber, el]) => {
            if (el && el.carMarkerRef.current) {
                rects[carNumber] = el.carMarkerRef.current.getBoundingClientRect();
            }
        });

        const result = {};

        Object.entries(rects).forEach(([idA, rectA]) => {
            const overlaps = [];

            Object.entries(rects).forEach(([idB, rectB]) => {
                if (idA === idB || rectA.width === 0) return;

                const left = rectA.left;
                const right = rectA.right;
                const top = rectA.top;
                const bottom = rectA.bottom;

                const item = { carPosition: rectB };

                const areaOfCurrent = (right - left) * (bottom - top);
                const areaOfItem = (item.carPosition.right - item.carPosition.left) * (item.carPosition.bottom - item.carPosition.top);

                const area = areaOfCurrent <= areaOfItem ? areaOfCurrent : areaOfItem;
                const percentage50 = area * 0.5;

                const XA1 = left;
                const YA1 = top;
                const XA2 = right;
                const YA2 = bottom;

                const XB1 = item.carPosition.left;
                const YB1 = item.carPosition.top;
                const XB2 = item.carPosition.right;
                const YB2 = item.carPosition.bottom;

                const intersectingArea = Math.max(0, Math.min(XA2, XB2) - Math.max(XA1, XB1)) * Math.max(0, Math.min(YA2, YB2) - Math.max(YA1, YB1));

                // overlap not possible
                if (left > item.carPosition.right || right < item.carPosition.left) return;
                if (top > item.carPosition.bottom || bottom < item.carPosition.top) return;

                let isOverlap = false;

                if (left >= item.carPosition.left && left <= item.carPosition.right) {
                    if (intersectingArea > percentage50) isOverlap = true;
                }

                if (right >= item.carPosition.left && right <= item.carPosition.right) {
                    if (intersectingArea > percentage50) isOverlap = true;
                }

                if (top >= item.carPosition.top && top <= item.carPosition.bottom) {
                    if (intersectingArea > percentage50) isOverlap = true;
                }

                if (bottom >= item.carPosition.top && bottom <= item.carPosition.bottom) {
                    if (intersectingArea > percentage50) isOverlap = true;
                }

                const carItem = carDataList.find((c) => c.Car.CarNumber.toString() === idB);

                if (carItem && isOverlap) overlaps.push(idB);
            });

            const keyCar = carDataList.find((c) => c.Car.CarNumber.toString() === idA.toString());

            const overlappingDisplay = [
                keyCar
                    ? {
                        carNumber: keyCar.Car.CarNumber,
                        car: keyCar,
                        holeNumber: keyCar.Car.Hole,
                        holeLocation: keyCar.Car.HoleLocation,
                        isKeyCar: true,
                    }
                    : null,
                ...overlaps.map((carNum) => {
                    const carItem = carDataList.find((c) => c.Car.CarNumber.toString() === carNum.toString());

                    if (!carItem) return null;

                    return {
                        carNumber: carItem.Car.CarNumber,
                        car: carItem,
                        holeNumber: carItem.Car.Hole,
                        holeLocation: carItem.Car.HoleLocation,
                        isKeyCar: false,
                    };
                }),
            ].filter(Boolean);

            result[idA] = {
                overlaps,
                overlappingDisplay,
            };
        });

        return result;
    }

    return (
        <>
            {siteLocationGlobalData.get() && mapData && mapsData &&
                siteLocationsData.map((locations, index) => {
                    const lat = locations?.CenterPointLatitude;
                    const lng = locations?.CenterPointLongitude;

                    return (
                        <AdvancedMarker position={{ lat, lng }} key={`Location Marker: ${locations.Id}, index: ${index}`} anchorLeft="50%" anchorTop="50%">
                            <LocationIcon
                                style={{ position: "relative", zIndex: 9 }}
                                tracksViewChanges={false}
                                key={"mapSiteId" + index}
                                uniqueId={"mapSiteIdUni" + index}
                                Id={locations?.Id}
                                lat={locations?.CenterPointLatitude}
                                lng={locations?.CenterPointLongitude}
                                locationName={locations?.Name}
                                IsEUSite={locations?.IsEUSite}
                            />
                        </AdvancedMarker>
                    )
                })
            }

            {shouldShowCarMarkers() ? (
                carData.map((carItem, i) => {
                    if (carItem?.Car?.Latitude !== 0 && carItem?.Car?.Longitude !== 0) {
                        var lat = carItem?.Car?.Latitude;
                        var lng = carItem?.Car?.Longitude;

                        return (<AdvancedMarker
                            position={{ lat, lng }}
                            key={`CarMarker-${carItem.Car.CarNumber}-${i++}`}
                            anchorLeft="50%"
                            anchorTop="50%"
                            zIndex={1}>
                            <MapCarIcon
                                ref={(el) => (markerRefs.current[carItem.Car.CarNumber] = el)}
                                carItem={carItem}
                                key={"carIconid" + i}
                                uniqueId={"carIconidUni" + i}
                                setEditModel={setEditModel}
                                mapsData={mapsData}
                                mapData={mapData}
                                overlapData={overlaps[carItem.Car.CarNumber]}
                                carProductData={carProductDataMap[carItem.Car.GpsiCarId]}
                            />
                        </AdvancedMarker>
                        );
                    }
                })
            ) : shouldShowTagCar() &&
            <TaggedCarIconComponent
                key={uuid()}
                uniqueId={uuid()}
                lat={tagCarFromFleetValue && tagCarFromFleetValue[0]?.lat}
                lng={tagCarFromFleetValue && tagCarFromFleetValue[0]?.lag}
            />
            }
        </>
    );
}